/**
 * US-95 D4: подсказка-назначение делегата при подаче отпуска.
 *
 * Всплывает ПОСЛЕ успешной отправки заявки на отпуск (плановый / за свой счёт),
 * если автор — ЛР и/или РП и у него на этот период ещё НЕ назначен делегат.
 * Инлайн-пикер: период уже подставлен датами отпуска, остаётся выбрать человека
 * и нажать «Назначить». Бэкенд — существующие setLrDelegate / setPmDelegate.
 */
import { useCallback, useRef, useState } from "react";
import { Alert, Button, Group, Modal, Select, Stack, Text } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import dayjs from "dayjs";
import {
  searchDelegateCandidates,
  setLrDelegate,
  setPmDelegate,
  type DelegationInput,
} from "../api/client";

function fmt(iso: string): string {
  const d = dayjs(iso);
  return d.isValid() ? d.format("DD.MM.YYYY") : iso;
}

interface SuggestBlockProps {
  title: string;
  hint: string;
  start: string; // ISO YYYY-MM-DD
  end: string;
  onSave: (body: DelegationInput) => Promise<unknown>;
  testid: string;
}

function SuggestBlock({ title, hint, start, end, onSave, testid }: SuggestBlockProps) {
  const [value, setValue] = useState<string | null>(null);
  const [options, setOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearch = useCallback((q: string) => {
    if (timer.current) clearTimeout(timer.current);
    if (q.trim().length < 2) return;
    timer.current = setTimeout(() => {
      searchDelegateCandidates(q.trim())
        .then((rows) =>
          setOptions((prev) => {
            const seen = new Set(prev.map((o) => o.value));
            const merged = [...prev];
            for (const r of rows) {
              if (!seen.has(String(r.id))) merged.push({ value: String(r.id), label: r.full_name });
            }
            return merged;
          }),
        )
        .catch(() => {
          /* поиск тихо игнорируем */
        });
    }, 250);
  }, []);

  const assign = useCallback(() => {
    if (!value) return;
    setSaving(true);
    setErr(null);
    onSave({ delegate_id: Number(value), active_from: start, active_to: end })
      .then(() => setDone(options.find((o) => o.value === value)?.label ?? "делегат"))
      .catch((e: Error) => setErr(e.message || "Не удалось назначить."))
      .finally(() => setSaving(false));
  }, [value, start, end, onSave, options]);

  if (done) {
    return (
      <Alert color="teal" title={title} data-testid={`${testid}-done`}>
        Назначен: <b>{done}</b> на {fmt(start)}–{fmt(end)}.
      </Alert>
    );
  }

  return (
    <Stack gap={6}>
      <Text fw={500} size="sm">
        {title}
      </Text>
      <Text size="xs" c="dimmed">
        {hint}
      </Text>
      <Group align="flex-end" gap="xs" wrap="nowrap">
        <Select
          style={{ flex: 1 }}
          placeholder="Выберите сотрудника"
          searchable
          clearable
          data={options}
          value={value}
          onChange={setValue}
          onSearchChange={handleSearch}
          nothingFoundMessage="Начните вводить ФИО…"
          aria-label={title}
          data-testid={`${testid}-select`}
        />
        <Button onClick={assign} loading={saving} disabled={!value || saving} data-testid={testid}>
          Назначить
        </Button>
      </Group>
      <Text size="xs" c="dimmed">
        На период отпуска: {fmt(start)}–{fmt(end)}
      </Text>
      {err && (
        <Text size="xs" c="red">
          {err}
        </Text>
      )}
    </Stack>
  );
}

export interface DelegateSuggestionModalProps {
  opened: boolean;
  onClose: () => void;
  start: string | null;
  end: string | null;
  showLr: boolean;
  showPm: boolean;
}

export function DelegateSuggestionModal({
  opened,
  onClose,
  start,
  end,
  showLr,
  showPm,
}: DelegateSuggestionModalProps) {
  const isMobile = useMediaQuery("(max-width: 48em)");
  if (!start || !end) return null;
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Назначить делегата на время отпуска?"
      centered={!isMobile}
      size="lg"
      fullScreen={isMobile}
    >
      <Stack gap="md">
        <Text size="sm">
          Заявка отправлена ✓. Пока вы в отпуске{" "}
          <b>
            {fmt(start)}–{fmt(end)}
          </b>
          , назначьте делегата, кто сможет в ваше отсутствие согласовывать заявки на отпуск и по ТШ.
          Период уже подставлен.
        </Text>
        {showLr && (
          <SuggestBlock
            title="Делегат по подчинённым (ЛР)"
            hint="Будет согласовывать заявки ваших подчинённых, пока вы в отпуске."
            start={start}
            end={end}
            onSave={setLrDelegate}
            testid="delegate-suggest-lr"
          />
        )}
        {showPm && (
          <SuggestBlock
            title="Делегат по проектам (РП)"
            hint="Будет согласовывать таймшиты по вашим проектам, пока вы в отпуске."
            start={start}
            end={end}
            onSave={setPmDelegate}
            testid="delegate-suggest-pm"
          />
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} data-testid="delegate-suggest-close">
            Готово
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
